/**
 * Header bar — design-reference lines 205–249. One of the five canonical surfaces (`kit.ts`).
 * The 52px titlebar: filter pills, the centred mode switch, session chips, the notification bell and the account menu.
 */
import type { ReactNode } from 'react';
import { css, s } from './style';
import type { Handler, Primitive } from './controls';

/** The titlebar itself (line 205). `.titlebar` carries the lift/condense transitions from the reference stylesheet. */
export const HEADER_BASE =
  'container-type:inline-size;position:relative;flex:none;height:52px;display:flex;align-items:center;gap:10px;margin:12px 12px 0;padding:0 8px 0 8px;border-radius:18px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-card);z-index:20';
export function HeaderBar({ children }: { children?: ReactNode }) {
  return (
    <header className="titlebar" data-titlebar style={css(HEADER_BASE)}>
      {children}
    </header>
  );
}

/** Filter pill — the Symbols / All Time controls (line 208, ×2). */
export function HeaderPill({
  icon,
  caret = true,
  onClick,
  hoverClass,
  children,
  ...aria
}: {
  icon?: string;
  caret?: boolean;
  onClick?: Handler;
  hoverClass?: string;
  children?: ReactNode;
  'aria-label'?: string;
}) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      style={css(
        'display:inline-flex;align-items:center;gap:4px;font-size:11px;padding:6px 9px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer;white-space:nowrap',
      )}
      {...aria}
    >
      {icon ? <i className={`ph ${icon}`} style={css('font-size:12px')}></i> : null}
      {children}
      {caret ? <i className="ph ph-caret-down" style={css('font-size:10px;color:var(--faint)')}></i> : null}
    </button>
  );
}

/** The centred mode capsule (line 212) — the workflow-mode switch. */
export function ModeSwitch({ children }: { children?: ReactNode }) {
  return (
    <div
      style={css(
        'position:absolute;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:6px;padding:4px 10px;border-radius:99px;background:var(--rail)',
      )}
    >
      {children}
    </div>
  );
}
/** A mode button inside the capsule (line 213). The active one carries the label; the rest are icon-only. */
export interface ModeButtonProps {
  icon: string;
  pad: Primitive;
  bg: Primitive;
  fg: Primitive;
  fw?: Primitive;
  onClick?: Handler;
  hoverClass?: string;
  title?: string;
  'aria-label'?: string;
  children?: ReactNode;
}
export function ModeButton({
  icon,
  pad,
  bg,
  fg,
  fw = '500',
  onClick,
  hoverClass,
  children,
  ...aria
}: ModeButtonProps) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      style={css(
        `display:inline-flex;align-items:center;gap:8px;padding:${s(pad)};border-radius:99px;border:none;background:${s(bg)};color:${s(fg)};font-size:12px;font-weight:${s(fw)};cursor:pointer;white-space:nowrap;flex:none`,
      )}
      {...aria}
    >
      <span
        style={css(
          `width:26px;height:26px;border-radius:50%;border:1px solid ${s(bg)};display:flex;align-items:center;justify-content:center;flex:none`,
        )}
      >
        <i className={`ph ${icon}`} style={css('font-size:14px')}></i>
      </span>
      {children}
    </button>
  );
}

/** Market-session chip — SYD / TYO / LON / NYC (line 219). */
export function SessionChip({
  dot,
  bd,
  fg,
  bg,
  name,
  time,
  cls,
}: {
  dot: Primitive;
  bd: Primitive;
  fg: Primitive;
  bg: Primitive;
  name: ReactNode;
  time?: ReactNode;
  cls?: string;
}) {
  return (
    <span
      className={cls}
      style={css(
        `display:inline-flex;align-items:center;gap:3px;font-size:9.5px;font-weight:600;letter-spacing:0.02em;padding:5px 6px;border-radius:99px;border:1px solid ${s(bd)};color:${s(fg)};background:${s(bg)};white-space:nowrap`,
      )}
    >
      <span style={css(`width:5px;height:5px;border-radius:50%;background:${s(dot)}`)}></span>
      {name}
      {time === undefined ? null : (
        <span style={css('opacity:0.75;font-weight:500;font-variant-numeric:tabular-nums')}>{time}</span>
      )}
    </span>
  );
}

/** Header icon button with an optional count badge — the notification bell (line 224). */
export function HeaderIconButton({
  icon,
  badge,
  onClick,
  hoverClass,
  ...aria
}: {
  icon: string;
  badge?: ReactNode;
  onClick?: Handler;
  hoverClass?: string;
  'aria-label'?: string;
  title?: string;
}) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      style={css(
        'position:relative;width:34px;height:34px;flex:none;border-radius:11px;border:1px solid var(--l8);background:transparent;color:var(--text-mid);cursor:pointer;display:flex;align-items:center;justify-content:center',
      )}
      {...aria}
    >
      <i className={`ph ${icon}`} style={css('font-size:15px')}></i>
      {badge === undefined ? null : (
        <span
          style={css(
            'position:absolute;top:-3px;right:-3px;min-width:15px;height:15px;padding:0 3px;border-radius:99px;background:var(--surface);color:var(--neg);font-size:9.5px;font-weight:700;display:flex;align-items:center;justify-content:center',
          )}
        >
          {badge}
        </span>
      )}
    </button>
  );
}
/** Vertical divider between header groups (line 226). */
export function HeaderDivider() {
  return <span style={css('width:1px;height:26px;background:var(--l8);flex:none;margin:0 2px')}></span>;
}

/** Account button + avatar (line 228). */
export function Avatar({
  size = 30,
  image,
  ink = 'var(--rail-ink)',
  initials,
}: {
  size?: 30 | 38;
  image?: Primitive;
  ink?: Primitive;
  initials?: ReactNode;
}) {
  return (
    <span
      style={css(
        `width:${size}px;height:${size}px;border-radius:50%;background:var(--rail);background-image:${s(image) || 'none'};background-size:cover;background-position:center;color:${s(ink)};display:flex;align-items:center;justify-content:center;font-size:${size === 30 ? '11' : '13'}px;font-weight:700;flex:none`,
      )}
    >
      {initials}
    </span>
  );
}
export function AccountButton({
  onClick,
  hoverClass,
  children,
  ...aria
}: {
  onClick?: Handler;
  hoverClass?: string;
  children?: ReactNode;
  'aria-label'?: string;
}) {
  return (
    <button
      className={hoverClass}
      onClick={onClick}
      style={css(
        'display:flex;align-items:center;gap:8px;padding:3px 10px 3px 3px;border-radius:99px;border:1px solid var(--l8);background:transparent;cursor:pointer',
      )}
      {...aria}
    >
      {children}
    </button>
  );
}

/** Account popover (line 237) and its rows (line 240, ×2). */
export function MenuPanel({ children }: { children?: ReactNode }) {
  return (
    <div
      role="menu"
      className="pf-pop"
      style={css(
        'position:absolute;top:calc(100% + 10px);right:0;width:262px;border-radius:16px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:14px;z-index:70',
      )}
    >
      {children}
    </div>
  );
}
export function MenuLink({
  icon,
  href = '#',
  hoverClass,
  children,
}: {
  icon?: string;
  href?: string;
  hoverClass?: string;
  children?: ReactNode;
}) {
  return (
    <a
      href={href}
      className={hoverClass}
      style={css(
        'display:flex;align-items:center;gap:9px;padding:9px 11px;border-radius:11px;font-size:12.5px;font-weight:500;color:var(--text-mid);text-decoration:none',
      )}
    >
      {icon ? <i className={`ph ${icon}`} style={css('font-size:15px;color:var(--muted)')}></i> : null}
      {children}
    </a>
  );
}
/** Plan chip in the account menu (line 235). */
export function PlanChip({ children, tone = 'var(--pos)' }: { children?: ReactNode; tone?: string }) {
  return (
    <span
      style={css(
        `font-size:9.5px;font-weight:600;letter-spacing:0.05em;padding:3px 9px;border-radius:99px;background:color-mix(in srgb, ${tone} 14%, transparent);color:${tone}`,
      )}
    >
      {children}
    </span>
  );
}
